import { useSnackbar } from '@/features/snackbar';
import { useFlag } from '@/features/unleash';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { useLocale } from '@/hooks/useLocale';
import routesGenerator from '@/router/routesGenerator';
import { isSupportedLocale } from '@/utils/locale';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { pathToAction, redirect } from 'redux-first-router';
import { useTracking } from 'react-tracking';
import { useQuery } from '@tanstack/react-query';
import { usePersistentStorage } from '@/hooks/usePersistentStorage';
import { useSystemLanguage } from './useSystemLanguage';

export function SupportedLocaleDetector() {
  const locale = useLocale();
  const { addSnackbar, removeSnackbar } = useSnackbar();
  const url = useCompanyCanonicalUrl();
  const dispatch = useDispatch();

  const { enabled } = useFlag({ name: 'supported-locale-detector' });

  const routesMap = useMemo(() => routesGenerator(), []);

  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'supported-locale-detector',
  });

  const { get, set } = usePersistentStorage();
  const { data, status } = useQuery({
    queryKey: ['supported-locale-detector'],
    async queryFn() {
      const value = await get('supported-locale-detector-status');
      return value === 'dismissed';
    },
  });

  const { lang } = useSystemLanguage();

  useEffect(() => {
    const languagePart = lang?.split('-')[0];
    if (
      typeof lang === 'string' &&
      status === 'success' &&
      !data &&
      enabled &&
      isSupportedLocale(languagePart) &&
      locale !== languagePart
    ) {
      addSnackbar({
        id: 'locale-available',
        message: 'This report is available in your preferred language setting.',
        actionLabel: 'View',
        lifeTime: 7000,
        async actionCallback() {
          trackEvent({
            action: 'click',
            modifier: 'preferredLanguage',
            locale: languagePart,
          });
          set('supported-locale-detector-status', 'dismissed');
          const action = pathToAction(`/${languagePart}${url}`, routesMap);
          dispatch(action);
        },
        secondaryActionLabel: 'Dismiss',
        secondaryActionCallback() {
          set('supported-locale-detector-status', 'dismissed');
        },
        layout: 'stack',
      });
    } else {
      removeSnackbar({ id: 'locale-available' });
    }
    /**
     * note: do not include `addSnackbar` in the dependency as it causes infinite renders.
     * maybe `addSnackbar` is not a stable reference.
     */
  }, [
    lang,
    data,
    status,
    locale,
    enabled,
    url,
    dispatch,
    redirect,
    trackEvent,
  ]);
  return <></>;
}
